<script setup lang="ts">
  import PaperclipIcon from '@/shared/assets/icons/PaperclipIcon.svg';
  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import { useFileDropzone } from '@/shared/lib';
  import { ColoredButton, IconImage } from '@/shared/ui';
  import { ATTACHMENT_ACCEPT } from '@/shared/config';
  import type { RecordAttachmentRow as AttachmentRow } from '../model/useRecordAttachments';
  import RecordAttachmentRow from './RecordAttachmentRow.vue';

  defineProps<{
    rows: AttachmentRow[];
    canAttach: boolean;
  }>();

  const emit = defineEmits<{
    attach: [files: File[]];
    open: [key: string];
    delete: [key: string];
  }>();

  const { fileInput, openFileDialog } = useFileDropzone();

  // The value is cleared after every choice: picking the same file again after a refusal would
  // otherwise fire no change event.
  function onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length > 0) emit('attach', files);
  }
</script>

<template>
  <section
    class="-mx-6 mt-6 flex flex-col gap-2.5 border-t border-gray-02 px-6 pt-8 pb-2"
    data-testid="record-attachments"
  >
    <div class="flex flex-col gap-4">
      <h2 class="flex items-center gap-2 text-sm/6 font-semibold text-gray-07">
        <span class="flex h-6 w-6 items-center justify-center rounded bg-gray-02">
          <IconImage
            :src="UploadIcon"
            alt=""
            aria-hidden="true"
            class="h-5 w-5"
          />
        </span>
        Anexos:
      </h2>
      <div
        v-if="canAttach"
        class="border-b border-gray-02 pb-6"
      >
        <ColoredButton
          data-testid="record-attachments-attach"
          text="Anexar arquivo"
          :icon="PaperclipIcon"
          icon-alt=""
          @click="openFileDialog"
        />
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          :accept="ATTACHMENT_ACCEPT"
          data-testid="record-attachments-input"
          @change="onFilesChosen"
        >
      </div>
    </div>

    <div
      v-if="rows.length > 0"
      class="flex flex-col gap-2"
      data-testid="record-attachments-list"
    >
      <p class="text-sm font-semibold text-gray-07">
        Arquivos enviados:
      </p>
      <ul class="flex flex-col rounded-2xl border border-gray-02 bg-white-01">
        <RecordAttachmentRow
          v-for="row in rows"
          :key="row.key"
          :row="row"
          @open="emit('open', $event)"
          @delete="emit('delete', $event)"
        />
      </ul>
    </div>
  </section>
</template>
